import { Modal, Row, Space, Steps, Table, Tabs, Typography, Select, Tooltip, Divider, Col } from "antd"
import { StyledButton } from "src/styled_components/StyledButton"
import useTranslate from "src/utils/useTranslate"
import { ShowNewParametersOrArchiveFoundStateTypes } from "./SelectExperiments"
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { StoreState } from "src/store/configureStore"
import { ExceptionOutlined, InfoCircleOutlined, PlusOutlined } from "@ant-design/icons"
import { ArchivedParametersContent } from "src/components/Common/ArchivedParametersFoundModal"
import { updateArchivedParametersRequest } from "src/store/actions/common"
import { AsyncStates } from "src/constants"
import { addRemoveIngredientsKeyToWorkOrderRequest } from "src/store/actions/workOrderDetails"
import { getDropdownFilteredValue } from "src/utils/decorator"


const { TabPane } = Tabs
const { Text } = Typography
const { Option } = Select


type NewParametersOrArchiveFoundModalPropsType = {
    parameterMapping: { [key: string]: any[] },
    setParameterMapping: Dispatch<SetStateAction<any>>,
    oldParameters: { [key: string]: any[] },
    showNewParametersOrArchiveFoundModal: ShowNewParametersOrArchiveFoundStateTypes,
    setShowNewParametersOrArchiveFoundModal: Dispatch<SetStateAction<ShowNewParametersOrArchiveFoundStateTypes>>,
    setSkipPrompt: Dispatch<SetStateAction<boolean>>
}


export const NewParametersOrArchiveFoundModal = ({ parameterMapping, setParameterMapping, oldParameters, showNewParametersOrArchiveFoundModal, setShowNewParametersOrArchiveFoundModal, setSkipPrompt }: NewParametersOrArchiveFoundModalPropsType) => {
    const dispatch = useDispatch()
    const [t] = useTranslate()

    const displayNames = useSelector((state: StoreState) => state.displayNames.data)
    const foundArchivedParameters = useSelector((state: StoreState) => state.common.foundArchivedParameters)
    const [currentStep, setCurrentStep] = useState(0)
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
    const workOrder = useSelector((state: StoreState) => state.workOrderDetails.workOrder || {});
    const [activeTabKey, setActiveTabKey] = useState(() => !!parameterMapping?.formulations?.length ? "formulations" : (!!parameterMapping?.processing?.length ? "processing" : (!!parameterMapping?.characterizations?.length ? "characterizations" : (!!parameterMapping?.properties?.length ? "properties" : ""))))

    const handleRemoveIngredients = () => {
        if (foundArchivedParameters.type === "work-order-fileupload" && workOrder?.work_order_id === foundArchivedParameters.id) {
            dispatch(addRemoveIngredientsKeyToWorkOrderRequest({
                remove_ingredient_ids: selectedRowKeys
            }))
            dispatch(updateArchivedParametersRequest({ inventory_ids: selectedRowKeys }))
            setSelectedRowKeys([])
        }
    }

    const handleClose = useCallback(() => {
        setSkipPrompt(true)
        setShowNewParametersOrArchiveFoundModal({
            newParametersFound: false,
            archivedParametersFound: false
        })
        setSelectedRowKeys([])
    }, [setShowNewParametersOrArchiveFoundModal, setSkipPrompt])

    useEffect(() => {
        if (!showNewParametersOrArchiveFoundModal.newParametersFound && !foundArchivedParameters.data.length) {
            handleClose()
        }
    }, [foundArchivedParameters.data.length, handleClose, showNewParametersOrArchiveFoundModal.newParametersFound])


    const handleNext = () => {
        if (showNewParametersOrArchiveFoundModal.newParametersFound) {
            setCurrentStep(1)
        }
    }

    const labelForSearchSupport = (label: any) => {
        return `${label.name} ${label.category || ""} ${label.sub_category || ""
            } ${label.supplier || ""} ${label.lot_no || ""} ${typeof label.costing === "string"
                ? label.costing
                : label.costing &&
                    label.costing.amount &&
                    label.costing.quantity &&
                    label.costing.unit
                    ? `${parseFloat(
                        (label.costing.amount / label.costing.quantity).toPrecision(5),
                    )}/${label.costing.unit}`
                    : ""
            }`.trim();
    };

    const columns = useMemo(() => [{
        dataIndex: "name",
        title: t("common.name"),
        key: "name",
    },
    {
        dataIndex: "category",
        title: t("common.category"),
        key: "category",
    }, {
        dataIndex: "parameter_mapped_to",
        title: <Space><Text>{t("dataUpload.parametersMappedTo")}</Text><Tooltip title={t("dataUpload.parametersTooltip")}><InfoCircleOutlined /></Tooltip></Space>,
        key: "parameter_mapped_to",
        render: (text: any, record: any) => {
            return <Select
                showSearch
                filterOption={(inputValue, options: any) => getDropdownFilteredValue(inputValue, options)}
                value={displayNames?.[activeTabKey === "formulations" ? "ingredients" : activeTabKey]?.[record?.parameter_mapped_to]?.name ?? record?.parameter_mapped_to}
                style={{ width: "100%" }} allowClear onChange={(e: any) => {
                    setParameterMapping((prevState: any) => {
                        const obj = { ...prevState }
                        obj[activeTabKey].forEach((res: any) => {
                            if (res.name === record.name && res.category === record.category) {
                                res.parameter_mapped_to = e
                            }
                        })
                        return obj
                    })
                }}>
                {Object.entries(displayNames?.[activeTabKey === "formulations" ? "ingredients" : activeTabKey] || {})
                    .filter(([key, value]: any) => !value?.is_archived && !oldParameters[activeTabKey].includes(key) && !parameterMapping[activeTabKey].map((res) => res?.parameter_mapped_to).includes(key))
                    .map(([key, value]: any, index: number) => (
                        <Option key={key + index} value={key} label={labelForSearchSupport(value)}>
                            <Row
                                style={{
                                    justifyContent: "space-between",
                                    gap: "10px",
                                    flexWrap: "nowrap",
                                    flexDirection: "row",
                                    maxWidth: 700
                                }}
                            >
                                <Col>
                                    <Text ellipsis={{ tooltip: String(value?.name) }} style={{ maxWidth: 400 }}>{String(value?.name)}</Text>
                                    {
                                        !!value?.lot_no ||
                                            !!value?.supplier?.trim() ||
                                            !!value?.sub_category?.trim() ||
                                            !!value?.category?.trim() ||
                                            !!value?.costing ? (
                                            <Row style={{ color: "#8c8c8c", gap: "0px 5px" }}>
                                                {!!value?.category?.trim() && (
                                                    <Text>
                                                        <Text style={{ color: "#bfbfbf" }}>{"Category : "}</Text>
                                                        <Text ellipsis={{ tooltip: value?.category }} style={{ maxWidth: 100 }}>{value?.category}</Text>
                                                    </Text>
                                                )}
                                                {!!value?.sub_category?.trim() && (
                                                    <Text>
                                                        <Text style={{ color: "#bfbfbf" }}>
                                                            {"Sub Category : "}
                                                        </Text>
                                                        <Text ellipsis={{ tooltip: value?.sub_category }} style={{ maxWidth: 100 }}>{value?.sub_category}</Text>
                                                    </Text>
                                                )}
                                                {!!value?.lot_no && (
                                                    <Text>
                                                        <Text style={{ color: "#bfbfbf" }}>{`${t("inventory.lot_no")} :`}</Text>
                                                        <Text ellipsis={{ tooltip: value?.lot_no }} style={{ maxWidth: 100 }}>{value?.lot_no}</Text>
                                                    </Text>
                                                )}
                                                {!!value?.supplier?.trim() && (
                                                    <Text>
                                                        <Text style={{ color: "#bfbfbf" }}>{"Supplier : "}</Text>
                                                        <Text ellipsis={{ tooltip: value?.supplier }} style={{ maxWidth: 100 }}>{value?.supplier}</Text>
                                                    </Text>
                                                )}
                                                {!!value?.costing &&
                                                    typeof value?.costing === "string" ? (
                                                    <Text>
                                                        <Text style={{ color: "#bfbfbf" }}>{"Costing : "}</Text>
                                                        <Text ellipsis={{ tooltip: value?.costing }} style={{ maxWidth: 100 }}>
                                                            {`${parseFloat(
                                                                Number(
                                                                    value?.costing,
                                                                ).toPrecision(),
                                                            )}`}
                                                        </Text>
                                                    </Text>
                                                ) : !!value?.costing &&
                                                    value?.costing.amount &&
                                                    value?.costing.quantity &&
                                                    value?.costing.unit ? (
                                                    <Text>
                                                        <Text style={{ color: "#bfbfbf" }}>{"Costing : "}</Text>
                                                        <Text ellipsis={{
                                                            tooltip: `${parseFloat(
                                                                (
                                                                    value?.costing.amount /
                                                                    value?.costing.quantity
                                                                ).toPrecision(5),
                                                            )}/${value?.costing.unit}`
                                                        }} style={{ maxWidth: 100 }}>{`${parseFloat(
                                                            (
                                                                value?.costing.amount /
                                                                value?.costing.quantity
                                                            ).toPrecision(5),
                                                        )}/${value?.costing.unit}`}</Text>
                                                    </Text>
                                                ) : null}
                                            </Row>
                                        ) : (
                                            <Row style={{ color: "#8c8c8c", gap: "10px" }}>
                                                <Text ellipsis={{ tooltip: value?.costing }} style={{ maxWidth: 100 }}>
                                                    <Text style={{ color: "#bfbfbf" }}>{"Category : "}</Text>
                                                    <Text>{"N/A"}</Text>
                                                </Text>
                                            </Row>
                                        )}
                                </Col>
                            </Row>
                            <Divider style={{ margin: "2px 0" }} />
                        </Option>
                    ))}
                <Option key="ignore" value={"ignore"}>{t("common.ignore")}</Option>
            </Select>
        },
        width: 400
    }
    ], [setParameterMapping, t, activeTabKey, displayNames, parameterMapping, oldParameters])


    const ArchivedParameterStep = () => {
        return <ArchivedParametersContent selectedRowKeys={selectedRowKeys} setSelectedRowKeys={setSelectedRowKeys} handleRemoveIngredients={handleRemoveIngredients} handleNext={handleNext} hideHandleNext={!showNewParametersOrArchiveFoundModal.newParametersFound} />
    }

    const NewParameterFoundStep = () => {
        return <Space direction="vertical" style={{ width: "100%", marginTop: 25 }} size="large">
            <Space direction="vertical" style={{ width: "100%" }} size="small">
                <Text>
                    {t("dataUpload.NewParametersDescription")}
                </Text>
            </Space>
            <Tabs size="large" activeKey={activeTabKey} onChange={setActiveTabKey}>
                {!!parameterMapping?.formulations?.length && (
                    <TabPane
                        tabKey="formulations"
                        tab={`${t("common.formulations")} (${parameterMapping?.formulations.length})`}
                        key="formulations"
                        style={{ width: "100%", overflow: "auto" }}
                    >
                        <Table
                            style={{ marginTop: 20 }}
                            columns={columns}
                            dataSource={parameterMapping.formulations}
                            bordered
                        // pagination={false}
                        />
                    </TabPane>
                )}
                {!!parameterMapping?.processing?.length && (
                    <TabPane
                        tabKey="processing"
                        tab={`${t("common.processing")} (${parameterMapping?.processing.length})`}
                        key="processing"
                        style={{ width: "100%", overflow: "auto" }}
                    >
                        <Table
                            style={{ marginTop: 20 }}
                            columns={columns}
                            dataSource={parameterMapping.processing}
                            bordered
                        // pagination={false}
                        />
                    </TabPane>
                )}
                {!!parameterMapping?.characterizations?.length && (
                    <TabPane
                        tab={`${t("common.characterisation")} (${parameterMapping?.characterizations?.length})`}
                        key="characterizations"
                        tabKey="characterizations"
                        style={{ width: "100%", overflow: "auto" }}
                    >
                        <Table
                            style={{ marginTop: 20 }}
                            columns={columns}
                            dataSource={parameterMapping.characterizations}
                            bordered
                        // pagination={false}
                        />
                    </TabPane>
                )}
                {!!parameterMapping?.properties?.length && (
                    <TabPane
                        tab={`${t("formulations.type.properties")} (${parameterMapping?.properties.length})`}
                        key="properties"
                        tabKey="properties"
                        style={{ width: "100%", overflow: "auto" }}
                    >
                        <Table
                            style={{ marginTop: 20 }}
                            columns={columns}
                            dataSource={parameterMapping.properties}
                            bordered
                        // pagination={false}
                        />
                    </TabPane>
                )}
            </Tabs>
            <Row justify={"end"} style={{ marginTop: 20 }}>
                <StyledButton onClick={() => {
                    handleClose()
                }} type="primary">{t("dataUpload.NewParametersContinue")}</StyledButton>
            </Row>
        </Space>
    }

    const getCurrentStepContent = () => {
        if (showNewParametersOrArchiveFoundModal.newParametersFound && showNewParametersOrArchiveFoundModal.archivedParametersFound) {
            if (currentStep === 0) {
                return <ArchivedParameterStep />
            }
            if (currentStep === 1) {
                return <NewParameterFoundStep />
            }
        } else if (showNewParametersOrArchiveFoundModal.archivedParametersFound) {
            return <ArchivedParameterStep />
        } else if (showNewParametersOrArchiveFoundModal.newParametersFound) {
            return <NewParameterFoundStep />
        }
    }


    return (
        <Modal
            open={showNewParametersOrArchiveFoundModal.newParametersFound || showNewParametersOrArchiveFoundModal.archivedParametersFound}
            onCancel={() => {
                setSelectedRowKeys([])
                setShowNewParametersOrArchiveFoundModal({
                    newParametersFound: false,
                    archivedParametersFound: false
                })
            }
            }
            width={1000}
            maskClosable={false}
            title={
                <div
                    style={{
                        width: "75%"
                    }}>
                    <Steps
                        current={currentStep}
                        onChange={(curr) => {
                            setCurrentStep(curr);
                        }}
                        items={[
                            ...(showNewParametersOrArchiveFoundModal.archivedParametersFound ? [{
                                title: `Found Archived Parameter`,
                                icon: <ExceptionOutlined />,
                                disabled: !foundArchivedParameters.data.length && foundArchivedParameters.status === AsyncStates.SUCCESS
                            }] : []),
                            ...(showNewParametersOrArchiveFoundModal.newParametersFound ? [{
                                title: t("dataUpload.NewParametersTitle"),
                                icon: <PlusOutlined />,
                                disabled: !!foundArchivedParameters.data.length && foundArchivedParameters.status === AsyncStates.SUCCESS
                            }] : []),
                        ]}
                    />
                </div>
            }
            footer={null}
        >
            <div>
                <div>{getCurrentStepContent()}</div>
            </div>
        </Modal>
    )
}
